import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group'
import { db } from '@/shared/lib/db'
import { cn } from '@/shared/lib/utils'

import { useAuthStore } from '../stores/auth.store'
import {
    completeOnboarding,
    FILMMAKING_EXPERTISE_LEVELS,
    FilmmakingExpertise,
} from '../services/userProfile'

export function OnboardingForm({ className, ...props }: React.ComponentProps<'form'>) {
    const navigate = useNavigate()
    const { user, setUser, setNeedsOnboarding } = useAuthStore()
    const existingName = useMemo(
        () => user?.name?.trim() || user?.displayName?.trim() || '',
        [user?.displayName, user?.name]
    )
    const shouldAskForName = !existingName

    const [name, setName] = useState(existingName)
    const [filmmakingExpertise, setFilmmakingExpertise] = useState<FilmmakingExpertise | ''>(
        (user?.filmmakingExpertise as FilmmakingExpertise | undefined) ?? ''
    )
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!user) {
            setError('Please log in again to continue.')
            return
        }

        const trimmedName = name.trim()

        if (shouldAskForName && !trimmedName) {
            setError('Please enter your name.')
            return
        }

        if (!filmmakingExpertise) {
            setError('Please select your filmmaking experience level.')
            return
        }

        setError('')
        setLoading(true)

        try {
            const profile = await completeOnboarding({
                name: trimmedName || undefined,
                filmmakingExpertise,
            })

            const nextUser = {
                uid: profile.uid,
                email: profile.email,
                displayName: profile.displayName,
                photoURL: profile.photoURL,
                name: profile.name,
                filmmakingExpertise: profile.filmmakingExpertise,
                onboardingCompleted: profile.onboardingCompleted,
            }

            await db.users.put(nextUser)
            setUser(nextUser)
            setNeedsOnboarding(false)
            toast.success('Profile saved')
            navigate('/dashboard', { replace: true })
        } catch (submitError) {
            console.error('Onboarding error:', submitError)
            setError('We could not save your profile. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <form onSubmit={handleSubmit} className={cn('flex flex-col gap-6', className)} {...props}>
            <div className="space-y-2 text-center">
                <h1 className="text-3xl font-bold tracking-tight">Tell us about you</h1>
                <p className="text-muted-foreground text-sm">
                    We&apos;ll use this to personalize your Storyverse workspace.
                </p>
            </div>

            {error && (
                <div className="rounded-md bg-red-50 p-3 text-center text-sm text-red-500">
                    {error}
                </div>
            )}

            {shouldAskForName ? (
                <div className="grid gap-3">
                    <Label htmlFor="name">Your name</Label>
                    <Input
                        id="name"
                        placeholder="Enter your name"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        disabled={loading}
                    />
                </div>
            ) : (
                <div className="rounded-xl border border-border/60 bg-muted/40 p-4 text-sm">
                    <p className="font-medium">You&apos;re signing in as {existingName}.</p>
                    <p className="text-muted-foreground mt-1">
                        We pulled your name from Firebase, so you can jump straight to your setup.
                    </p>
                </div>
            )}

            <div className="grid gap-3">
                <Label>Your filmmaking expertise</Label>
                <RadioGroup
                    value={filmmakingExpertise}
                    onValueChange={(value) =>
                        setFilmmakingExpertise(value as FilmmakingExpertise)
                    }
                    className="gap-3"
                >
                    {FILMMAKING_EXPERTISE_LEVELS.map((level) => (
                        <Label
                            key={level.value}
                            htmlFor={level.value}
                            className="flex cursor-pointer items-start gap-3 rounded-xl border border-border/70 p-4 transition hover:border-primary/50"
                        >
                            <RadioGroupItem id={level.value} value={level.value} className="mt-1" />
                            <span className="space-y-1">
                                <span className="block font-medium">{level.label}</span>
                                <span className="text-muted-foreground block text-sm">
                                    {level.description}
                                </span>
                            </span>
                        </Label>
                    ))}
                </RadioGroup>
            </div>

            <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Continue to home'}
            </Button>
        </form>
    )
}
